import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useAppStore } from '@/store/appStore'

interface LayoutProps {
  children: React.ReactNode
}

const navItems = [
  {
    label: 'Dashboard',
    href: '/',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    label: 'Knowledge Assets',
    href: '/assets',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
  },
  {
    label: 'Settings',
    href: '/settings',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
]

const getPageTitle = (pathname: string): string => {
  if (pathname.startsWith('/rfp/')) return 'SprintBid'
  if (pathname === '/dev') return 'Developer Diagnostics'
  const item = navItems.find((item) => item.href === pathname)
  return item?.label || 'SprintBid'
}

const isActive = (pathname: string, href: string): boolean => {
  if (href === '/') return pathname === '/'
  return pathname === href
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const logout = useAppStore((state) => state.logout)
  const devEnabled = useAppStore((state) => state.devMode)
  const collapsed = useAppStore((state) => state.sidebarCollapsed)
  const currentRfpTitle = useAppStore((state) => state.currentRfpTitle)
  const setCollapsed = useAppStore((state) => state.setSidebarCollapsed)
  const [scrolled, setScrolled] = React.useState(false)
  const [logoutBusy, setLogoutBusy] = React.useState(false)
  const [mobileOpen, setMobileOpen] = React.useState(false)

  React.useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  React.useEffect(() => {
    const el = document.getElementById('main-scroll')
    if (!el) return
    const onScroll = () => setScrolled(el.scrollTop > 4)
    el.addEventListener('scroll', onScroll)
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  const visibleNavItems = devEnabled
    ? [...navItems, {
        label: 'Developer',
        href: '/dev',
        icon: (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
        ),
      }]
    : navItems

  const handleNewRfp = () => {
    setMobileOpen(false)
    navigate('/', { state: { openCreate: true } })
  }
  const handleLogout = async () => {
    if (logoutBusy) return
    setLogoutBusy(true)
    const accessToken = localStorage.getItem('accessToken') || ''
    const refreshToken = localStorage.getItem('refreshToken') || ''
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`
    }
    try {
      await fetch('/api/v1/auth/logout', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
    } catch {
      // Ignore network/logout endpoint errors and always clear local session.
    } finally {
      logout()
      navigate('/login', { replace: true })
      setLogoutBusy(false)
    }
  }

  const isRfpDetail = location.pathname.startsWith('/rfp/')
  // The page's own header carries the RFP title, so the breadcrumb stays the short id.
  const pageTitle = isRfpDetail
    ? `RFP #${location.pathname.split('/')[2] || ''}`
    : getPageTitle(location.pathname)
  const pageTitleHint = isRfpDetail ? currentRfpTitle.trim() || pageTitle : pageTitle

  const sidebar = (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className={clsx('flex h-14 items-center gap-3 px-4', collapsed && 'lg:justify-center lg:px-0')}>
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-indigo-500 shadow-lg shadow-primary-900/40">
          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div className={clsx('min-w-0', collapsed && 'lg:hidden')}>
          <p className="font-display text-[15px] font-bold leading-tight text-white">SprintBid</p>
          <p className="text-[11px] leading-tight text-slate-400">AI-Powered Proposal Pipeline</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="mt-2 flex-1 space-y-0.5 px-3" aria-label="Main">
        <p className={clsx('px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500', collapsed && 'lg:hidden')}>
          Workspace
        </p>
        {visibleNavItems.map((item) => {
          const active = isActive(location.pathname, item.href) || (item.href === '/' && isRfpDetail)
          return (
            <Link
              key={item.href}
              to={item.href}
              title={item.label}
              aria-current={active ? 'page' : undefined}
              className={clsx(
                'group relative flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors duration-150',
                collapsed && 'lg:justify-center lg:px-0',
                active ? 'bg-white/10 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'
              )}
            >
              {active && <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-primary-500" />}
              <span className={clsx('flex-shrink-0', active ? 'text-primary-200' : 'text-slate-500 group-hover:text-slate-300')}>
                {item.icon}
              </span>
              <span className={clsx(collapsed && 'lg:hidden')}>{item.label}</span>
            </Link>
          )
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-white/5 p-3">
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          className={clsx(
            'hidden w-full items-center gap-3 rounded-xl px-3 py-2 text-xs font-medium text-slate-500 transition hover:bg-white/5 hover:text-slate-200 lg:flex',
            collapsed && 'lg:justify-center lg:px-0'
          )}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg className={clsx('h-4 w-4 flex-shrink-0 transition-transform', collapsed && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
          <span className={clsx(collapsed && 'lg:hidden')}>Collapse</span>
        </button>
        <p className={clsx('px-3 pt-2 text-[11px] text-slate-600', collapsed && 'lg:hidden')}>Jaya Consulting Inc.</p>
      </div>
    </div>
  )

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--bg-base)] text-slate-900">
      {/* ── Sidebar (desktop) ── */}
      <aside
        className={clsx(
          'hidden flex-shrink-0 border-r border-white/5 bg-[var(--bg-sidebar)] transition-[width] duration-200 lg:block',
          collapsed ? 'w-[72px]' : 'w-64'
        )}
      >
        {sidebar}
      </aside>

      {/* ── Sidebar (mobile drawer) ── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-950/50 animate-fade-in" onClick={() => setMobileOpen(false)} />
          <aside className="relative h-full w-64 bg-[var(--bg-sidebar)] shadow-pop animate-slide-in-left">{sidebar}</aside>
        </div>
      )}

      {/* ── Main content ── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header
          className={clsx(
            'z-20 flex h-14 flex-shrink-0 items-center justify-between gap-4 border-b bg-white/80 px-4 backdrop-blur-xl transition-shadow duration-200 sm:px-6',
            scrolled ? 'border-slate-200 shadow-sm' : 'border-slate-200/70'
          )}
        >
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="bp-icon-btn lg:hidden"
              aria-label="Open navigation"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Link to="/" className="flex flex-shrink-0 items-center gap-2" aria-label="SprintBid home">
              <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-indigo-500 shadow-sm">
                <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </span>
              <span className="hidden font-display text-sm font-bold text-slate-900 sm:inline">SprintBid</span>
            </Link>
            <div className="hidden h-6 w-px flex-shrink-0 bg-slate-200 sm:block" />
            <nav className="flex min-w-0 items-center gap-1.5 text-sm" aria-label="Breadcrumb">
              <Link
                to="/"
                className={clsx(
                  'flex-shrink-0 truncate font-medium transition-colors',
                  location.pathname === '/' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'
                )}
              >
                Dashboard
              </Link>
              {location.pathname !== '/' && (
                <>
                  <svg className="h-4 w-4 flex-shrink-0 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="truncate font-medium text-slate-900" title={pageTitleHint}>
                    {pageTitle}
                  </span>
                </>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleNewRfp}
              className="inline-flex h-9 items-center gap-2 rounded-xl bg-primary-600 px-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">New RFP</span>
            </button>
            <div className="mx-1 hidden h-6 w-px bg-slate-200 sm:block" />
            <button
              type="button"
              onClick={handleLogout}
              disabled={logoutBusy}
              title="Sign out"
              className="inline-flex h-9 items-center gap-2 rounded-xl px-3 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H9m4 4v1a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h5a2 2 0 012 2v1" />
              </svg>
              <span className="hidden sm:inline">{logoutBusy ? 'Logging out...' : 'Logout'}</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main id="main-scroll" className="flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-4 sm:px-6 lg:px-8 lg:py-5">{children}</div>
        </main>
      </div>
    </div>
  )
}
